import type {
  PermissionActionSemantics,
  PermissionCapability,
  PermissionEntry,
  PermissionGroup,
  PermissionScope,
} from "./types";

export type PermissionKey = string;
export type OneOfChoices = Record<string, PermissionKey>;

export type PermissionDependencyResolution = {
  effective: Set<PermissionKey>;
  requiredBy: Map<PermissionKey, Set<PermissionKey>>;
  oneOfChoices: OneOfChoices;
};

export const capabilityKey = ({ resource, action, scope }: PermissionCapability): PermissionKey =>
  `${resource}|${action}|${scope}`;

export const parseCapabilityKey = (key: PermissionKey): PermissionCapability => {
  const [resource, action, scope = "ANY"] = key.split("|");
  return { resource, action, scope: scope as PermissionScope };
};

export const semanticsFor = (
  entry: PermissionEntry,
  action: string,
  scope: PermissionScope
): PermissionActionSemantics | undefined =>
  entry.semantics?.[action]?.find((item) => item.scope === scope);

export const actionScopes = (entry: PermissionEntry, action: string): PermissionScope[] => {
  const scopes = entry.semantics?.[action]
    ?.map((item) => item.scope)
    .filter(Boolean);
  return Array.from(new Set(scopes?.length ? scopes : [entry.scope || "ANY"])) as PermissionScope[];
};

const dependencyIndex = (catalog: PermissionGroup[]) => {
  const index = new Map<PermissionKey, PermissionActionSemantics>();
  catalog.forEach((group) => group.entries.forEach((entry) => {
    entry.actions.forEach((action) => {
      actionScopes(entry, action).forEach((scope) => {
        index.set(capabilityKey({ resource: entry.resource, action, scope }), semanticsFor(entry, action, scope) || {
          scope,
          implies: [],
          requires: [],
          oneOf: [],
        });
      });
    });
  }));
  return index;
};

export const oneOfChoiceId = (source: PermissionKey, groupIndex: number) => `${source}#${groupIndex}`;

export const resolvePermissionDependencies = (
  direct: Set<PermissionKey>,
  catalog: PermissionGroup[],
  requestedChoices: OneOfChoices = {}
): PermissionDependencyResolution => {
  const index = dependencyIndex(catalog);
  const effective = new Set<PermissionKey>(direct);
  const requiredBy = new Map<PermissionKey, Set<PermissionKey>>();
  const oneOfChoices: OneOfChoices = {};
  const queue = Array.from(direct);

  const addDependency = (target: PermissionKey, source: PermissionKey) => {
    if (!requiredBy.has(target)) requiredBy.set(target, new Set());
    requiredBy.get(target)!.add(source);
    if (!effective.has(target)) {
      effective.add(target);
      queue.push(target);
    }
  };

  while (queue.length > 0) {
    const source = queue.shift()!;
    const sourceCapability = parseCapabilityKey(source);
    const semantics = index.get(source);
    if (!semantics) continue;

    semantics.implies.forEach((action) => addDependency(
      capabilityKey({ ...sourceCapability, action }),
      source
    ));
    semantics.requires.forEach((requirement) => addDependency(capabilityKey(requirement), source));
    semantics.oneOf.forEach((alternatives, groupIndex) => {
      const alternativesKeys = alternatives.map(capabilityKey);
      const choiceId = oneOfChoiceId(source, groupIndex);
      const requested = requestedChoices[choiceId];
      const selected = requested && alternativesKeys.includes(requested)
        ? requested
        : alternativesKeys.find((key) => effective.has(key)) || alternativesKeys[0];
      if (!selected) return;
      oneOfChoices[choiceId] = selected;
      addDependency(selected, source);
    });
  }

  return { effective, requiredBy, oneOfChoices };
};

export const dependencyLabel = (key: PermissionKey) => {
  const { resource, action, scope } = parseCapabilityKey(key);
  return `${resource}:${action}:${scope}`;
};

export const persistedPermissionKeys = (
  direct: Set<PermissionKey>,
  resolution: PermissionDependencyResolution
) => new Set<PermissionKey>([
  ...Array.from(direct),
  ...Object.values(resolution.oneOfChoices),
]);
